import { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('midgard_token'));
  const [loading, setLoading] = useState(true);
  const [betaMode, setBetaMode] = useState(false);
  const [betaLocked, setBetaLocked] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }

      // Always fetch beta status so the gate can show even before auth resolves
      try {
        const betaRes = await api.get('/beta/status');
        setBetaMode(betaRes.data.private_beta === true);
      } catch {
        // Non-fatal — proceed without beta gate if this fails
      }

      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
          setBetaLocked(false);
        } catch (error) {
          if (error.response?.data?.detail === 'private_beta') {
            // Valid token but account not on the approved list
            setBetaLocked(true);
          } else {
            console.error('Auth init error:', error);
            localStorage.removeItem('midgard_token');
            setToken(null);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('midgard_token', newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const loginWithToken = (newToken, userData) => {
    localStorage.setItem('midgard_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const register = async (username, email, password, displayName, acceptedDocuments = null) => {
    const response = await api.post('/auth/register', {
      username,
      email,
      password,
      display_name: displayName,
      ...(acceptedDocuments ? { accepted_documents: acceptedDocuments } : {}),
    });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('midgard_token', newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('midgard_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      betaMode,
      betaLocked,
      login,
      loginWithToken,
      register,
      logout,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
